import React from 'react';
import {
  useGetMatchDetailsQuery,
  UserMatch,
  FEEDBACK_LABELS,
  SUGGESTION_TYPE_LABELS,
  Feedback,
  SuggestionType,
} from 'src/store/matchTestApi';
import { Badge } from 'src/ui/Badge';
import { Spinner } from 'src/ui/Spinner';
import { Alert } from 'src/ui/Alert';
import { Card } from 'src/ui/Card';

interface MatchDetailsProps {
  matchId?: number;
  match?: UserMatch | null;
}

// Helper sub-components for compact layout
const StatRow = ({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: string;
}) => (
  <div className="row" style={{ justifyContent: 'space-between', gap: 8 }}>
    <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}>
      {label}
    </span>
    <span
      style={{
        fontSize: 'var(--fs-sm)',
        fontWeight: 600,
        color: accent ?? 'var(--text)',
      }}
    >
      {value}
    </span>
  </div>
);

const SuggestionBlock = ({
  label,
  sugg,
}: {
  label: string;
  sugg: {
    type: SuggestionType;
    isViewed: boolean;
    feedback: Feedback | null;
    reason?: string;
  };
}) => (
  <div className="col" style={{ gap: 6 }}>
    <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 500 }}>{label}</span>
    <div className="col" style={{ gap: 4 }}>
      <div
        className="row"
        style={{ justifyContent: 'space-between', alignItems: 'center' }}
      >
        <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}>
          Type:
        </span>
        <Badge
          size="sm"
          color={sugg.type === SuggestionType.MATCH ? 'blue' : 'purple'}
        >
          {SUGGESTION_TYPE_LABELS[sugg.type] || 'Unknown'}
        </Badge>
      </div>
      <div
        className="row"
        style={{ justifyContent: 'space-between', alignItems: 'center' }}
      >
        <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}>
          Viewed:
        </span>
        <Badge size="sm" color={sugg.isViewed ? 'green' : 'gray'}>
          {sugg.isViewed ? 'Yes' : 'No'}
        </Badge>
      </div>
      {sugg.feedback !== null && (
        <div
          className="row"
          style={{ justifyContent: 'space-between', alignItems: 'center' }}
        >
          <span
            style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}
          >
            Feedback:
          </span>
          <Badge
            size="sm"
            color={sugg.feedback === Feedback.LIKE ? 'green' : 'red'}
          >
            {FEEDBACK_LABELS[sugg.feedback as Feedback]}
          </Badge>
        </div>
      )}
      {sugg.reason && (
        <div className="col" style={{ gap: 2 }}>
          <span
            style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}
          >
            Reason:
          </span>
          <span
            style={{
              fontSize: 'var(--fs-sm)',
              whiteSpace: 'pre-wrap',
              color: 'var(--text)',
            }}
          >
            {sugg.reason}
          </span>
        </div>
      )}
    </div>
  </div>
);

const getStatusColor = (status: any): string => {
  if (!status || typeof status !== 'string') return 'gray';
  switch (status.toLowerCase()) {
    case 'matched':
      return 'green';
    case 'pending':
      return 'amber';
    case 'rejected':
      return 'red';
    default:
      return 'gray';
  }
};

const formatScore = (score: number) => (score * 100).toFixed(1) + '%';
const formatDate = (d: string) => new Date(d).toLocaleString();

const MatchDetails = ({ matchId, match }: MatchDetailsProps) => {
  const {
    data: matchDetails,
    isLoading,
    error,
  } = useGetMatchDetailsQuery(matchId as number, { skip: !matchId });

  if (!matchId || !match) {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <div
          style={{
            padding: 16,
            borderBottom: '1px solid var(--border)',
            flexShrink: 0,
          }}
        >
          <span
            style={{
              fontWeight: 600,
              fontSize: 'var(--fs-md)',
              color: 'var(--text-muted)',
            }}
          >
            Match Details
          </span>
        </div>
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <span
            style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-sm)' }}
          >
            Select a match to view details
          </span>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <div
          style={{
            padding: 16,
            borderBottom: '1px solid var(--border)',
            flexShrink: 0,
          }}
        >
          <span
            style={{
              fontWeight: 600,
              fontSize: 'var(--fs-md)',
              color: 'var(--text)',
            }}
          >
            Match Details
          </span>
        </div>
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Spinner size="lg" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <div
          style={{
            padding: 16,
            borderBottom: '1px solid var(--border)',
            flexShrink: 0,
          }}
        >
          <span
            style={{
              fontWeight: 600,
              fontSize: 'var(--fs-md)',
              color: 'var(--text)',
            }}
          >
            Match Details
          </span>
        </div>
        <div style={{ padding: 16 }}>
          <Alert color="red">Failed to load match details</Alert>
        </div>
      </div>
    );
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Fixed Header */}
      <div
        style={{
          padding: 16,
          borderBottom: '1px solid var(--border)',
          flexShrink: 0,
        }}
      >
        <div
          className="row"
          style={{ justifyContent: 'space-between', alignItems: 'center' }}
        >
          <span
            style={{
              fontWeight: 600,
              fontSize: 'var(--fs-md)',
              color: 'var(--text)',
            }}
          >
            Match Details
          </span>
          <Badge color={getStatusColor(match.status) as any}>
            {match.status}
          </Badge>
        </div>
        <div
          style={{
            fontSize: 'var(--fs-sm)',
            color: 'var(--text-muted)',
            marginTop: 2,
          }}
        >
          Match ID: {matchId}
        </div>
      </div>

      {/* Scrollable Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        <div className="col" style={{ gap: 12 }}>
          {/* Match Scores */}
          <Card pad>
            <div className="col" style={{ gap: 8 }}>
              <span
                style={{
                  fontWeight: 600,
                  fontSize: 'var(--fs-sm)',
                  color: 'var(--text)',
                  marginBottom: 4,
                }}
              >
                Match Scores
              </span>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 8,
                }}
              >
                <div className="col" style={{ gap: 2 }}>
                  <span
                    style={{
                      fontSize: 'var(--fs-xs)',
                      color: 'var(--text-muted)',
                    }}
                  >
                    User Score
                  </span>
                  <span
                    style={{
                      fontSize: 'var(--fs-lg)',
                      fontWeight: 700,
                      color: 'var(--accent)',
                    }}
                  >
                    {formatScore(match.scores.userScore)}
                  </span>
                </div>
                <div className="col" style={{ gap: 2 }}>
                  <span
                    style={{
                      fontSize: 'var(--fs-xs)',
                      color: 'var(--text-muted)',
                    }}
                  >
                    Match Score
                  </span>
                  <span
                    style={{
                      fontSize: 'var(--fs-lg)',
                      fontWeight: 700,
                      color: 'var(--green-solid)',
                    }}
                  >
                    {formatScore(match.scores.matchedUserScore)}
                  </span>
                </div>
              </div>
            </div>
          </Card>

          {matchDetails && (
            <>
              {/* Match Information */}
              <Card pad>
                <div className="col" style={{ gap: 8 }}>
                  <span
                    style={{
                      fontWeight: 600,
                      fontSize: 'var(--fs-sm)',
                      marginBottom: 4,
                    }}
                  >
                    Match Information
                  </span>
                  <div
                    className="row"
                    style={{
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <span
                      style={{
                        fontSize: 'var(--fs-sm)',
                        color: 'var(--text-muted)',
                      }}
                    >
                      Primary Match:
                    </span>
                    <Badge color={matchDetails.isPrimary ? 'green' : 'gray'}>
                      {matchDetails.isPrimary ? 'Yes' : 'No'}
                    </Badge>
                  </div>
                  <hr
                    style={{
                      border: 'none',
                      borderTop: '1px solid var(--border)',
                      margin: 0,
                    }}
                  />
                  <div className="col" style={{ gap: 2 }}>
                    <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 500 }}>
                      User A (Original):
                    </span>
                    <span
                      style={{
                        fontSize: 'var(--fs-sm)',
                        color: 'var(--text-secondary)',
                      }}
                    >
                      {matchDetails.userA.name || matchDetails.userA.email}
                    </span>
                    <span
                      style={{
                        fontSize: 'var(--fs-xs)',
                        color: 'var(--text-muted)',
                      }}
                    >
                      {matchDetails.userA.email} • {matchDetails.userA.gender}
                    </span>
                  </div>
                  <div className="col" style={{ gap: 2 }}>
                    <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 500 }}>
                      User B (Matched):
                    </span>
                    <span
                      style={{
                        fontSize: 'var(--fs-sm)',
                        color: 'var(--text-secondary)',
                      }}
                    >
                      {matchDetails.userB.name || matchDetails.userB.email}
                    </span>
                    <span
                      style={{
                        fontSize: 'var(--fs-xs)',
                        color: 'var(--text-muted)',
                      }}
                    >
                      {matchDetails.userB.email} • {matchDetails.userB.gender}
                    </span>
                  </div>
                </div>
              </Card>

              {/* Detailed Scores */}
              <Card pad>
                <div className="col" style={{ gap: 8 }}>
                  <span
                    style={{
                      fontWeight: 600,
                      fontSize: 'var(--fs-sm)',
                      marginBottom: 4,
                    }}
                  >
                    Detailed Scores
                  </span>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 8,
                    }}
                  >
                    <div className="col" style={{ gap: 2 }}>
                      <span
                        style={{
                          fontSize: 'var(--fs-xs)',
                          color: 'var(--text-muted)',
                        }}
                      >
                        User A Score
                      </span>
                      <span
                        style={{
                          fontSize: 'var(--fs-lg)',
                          fontWeight: 700,
                          color: 'var(--text)',
                        }}
                      >
                        {formatScore(matchDetails.scores.userAScore)}
                      </span>
                    </div>
                    <div className="col" style={{ gap: 2 }}>
                      <span
                        style={{
                          fontSize: 'var(--fs-xs)',
                          color: 'var(--text-muted)',
                        }}
                      >
                        User B Score
                      </span>
                      <span
                        style={{
                          fontSize: 'var(--fs-lg)',
                          fontWeight: 700,
                          color: 'var(--text)',
                        }}
                      >
                        {formatScore(matchDetails.scores.userBScore)}
                      </span>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Match Reasons */}
              {(matchDetails.reasons.reasonForUserA ||
                matchDetails.reasons.reasonForUserB) && (
                <Card pad>
                  <div className="col" style={{ gap: 8 }}>
                    <span
                      style={{
                        fontWeight: 600,
                        fontSize: 'var(--fs-sm)',
                        marginBottom: 4,
                      }}
                    >
                      Match Reasons
                    </span>
                    {matchDetails.reasons.reasonForUserA && (
                      <div className="col" style={{ gap: 2 }}>
                        <span
                          style={{ fontSize: 'var(--fs-sm)', fontWeight: 500 }}
                        >
                          Reason for User A:
                        </span>
                        <span
                          style={{
                            fontSize: 'var(--fs-sm)',
                            color: 'var(--text-secondary)',
                            whiteSpace: 'pre-wrap',
                          }}
                        >
                          {matchDetails.reasons.reasonForUserA}
                        </span>
                      </div>
                    )}
                    {matchDetails.reasons.reasonForUserB && (
                      <div className="col" style={{ gap: 2 }}>
                        <span
                          style={{ fontSize: 'var(--fs-sm)', fontWeight: 500 }}
                        >
                          Reason for User B:
                        </span>
                        <span
                          style={{
                            fontSize: 'var(--fs-sm)',
                            color: 'var(--text-secondary)',
                            whiteSpace: 'pre-wrap',
                          }}
                        >
                          {matchDetails.reasons.reasonForUserB}
                        </span>
                      </div>
                    )}
                  </div>
                </Card>
              )}

              {/* Suggestions */}
              {matchDetails.suggestions &&
                (matchDetails.suggestions.suggestionForUserA ||
                  matchDetails.suggestions.suggestionForUserB) && (
                  <Card pad>
                    <div className="col" style={{ gap: 12 }}>
                      <span
                        style={{
                          fontWeight: 600,
                          fontSize: 'var(--fs-sm)',
                          marginBottom: 4,
                        }}
                      >
                        Suggestions
                      </span>
                      {matchDetails.suggestions.suggestionForUserA && (
                        <SuggestionBlock
                          label="Suggestion for User A:"
                          sugg={matchDetails.suggestions.suggestionForUserA}
                        />
                      )}
                      {matchDetails.suggestions.suggestionForUserB && (
                        <SuggestionBlock
                          label="Suggestion for User B:"
                          sugg={matchDetails.suggestions.suggestionForUserB}
                        />
                      )}
                    </div>
                  </Card>
                )}

              {/* Timestamps */}
              <Card pad>
                <div className="col" style={{ gap: 8 }}>
                  <span
                    style={{
                      fontWeight: 600,
                      fontSize: 'var(--fs-sm)',
                      marginBottom: 4,
                    }}
                  >
                    Timeline
                  </span>
                  <StatRow
                    label="Created:"
                    value={formatDate(matchDetails.timestamps.createdAt)}
                  />
                  <StatRow
                    label="Updated:"
                    value={formatDate(matchDetails.timestamps.updatedAt)}
                  />
                </div>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MatchDetails;
